import { Box, Stack, Tooltip } from "@mui/material";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { getWhatsappContactNameById, selectWhatsappContactById } from "@common-reducers/WhatsAppSelectors";
import { WhatsappContact } from "@wa-communication-layer/whatsapp";
import ContactAvatar from '@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import ScheduledMessagesCount from "@common-components/scheduled-messages-count/scheduled-messages-count";
import moment from "moment";
import React from "react";
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface WhatsappContactProps {
  whatsappId?: string;
  contactName?: string;
  message?: string;
  messageScheduledCount?: number;
  messageApiCount?: number;
  messageCampaignCount?: number;
  dueDate?: string;
  justifyContentType?: 'space-between' | 'center',
  showMessageDetails?: boolean
  profilePicURL?: string;
}

const WhatsappContactBox = ({ message, contactName: _contactName, whatsappId, messageScheduledCount, messageApiCount, messageCampaignCount, dueDate, justifyContentType = 'space-between', showMessageDetails = true, profilePicURL }: WhatsappContactProps) => {
  const contactName = useAppSelector(state => getWhatsappContactNameById(state, whatsappId, _contactName));
  const formattedDate = dueDate ? moment(dueDate).format("HH:mm DD/MM/yyyy") : '';

  // Custom tooltip component
  const TooltipContent = () => (
    <Box
      sx={{
        padding: 1.5,
        maxWidth: 300,
        wordBreak: 'break-word',
      }}
    >
      <Box sx={{ mb: 2 }}>
        {message || '\u00A0'}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          fontSize: '12px',
          fontWeight: 500,
        }}
      >
        {formattedDate}
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={<TooltipContent />}
      arrow
      placement="top"
      enterDelay={500}
      leaveDelay={200}
    >
      <Box sx={{ 
        width: "100%",
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 1.5,
      }}>
        {/* Avatar (green area) */}
        <ContactAvatar
          sx={{ 
            flexShrink: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
          }}
          contactId={whatsappId}
          size={40}
          textSize={11}
          profilePicURL={profilePicURL}
        />
        
        {showMessageDetails && (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            width: 'calc(100% - 56px)', // Account for avatar width and gap
            gap: 1
          }}>
            {/* Text box (red area) */}
            <Box 
              sx={{ 
                overflow: 'hidden',
                minWidth: 0,
                width: '100%'
              }}
            >
              <Box sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-color)',
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                lineHeight: 1.2,
                mb: 0.5
              }}>
                {contactName}
              </Box>
              <Box sx={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                lineHeight: 1.2,
              }}>
                {message || '\u00A0'}
              </Box>
            </Box>
            
            {/* Bottom row with counters and date badge (blue area) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between'
              }}
            >
              {/* Counters */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {messageScheduledCount ? <ScheduledMessagesCount title="SCHEDULED" countValue={messageScheduledCount} /> : null}
                {messageApiCount ? <ScheduledMessagesCount title="API" countValue={messageApiCount} /> : null}
                {messageCampaignCount ? <ScheduledMessagesCount title="CAMPAIGN" countValue={messageCampaignCount} /> : null}
              </Box>
              
              {/* Date (blue area) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'rgba(232, 245, 253, 0.8)',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#1976d2',
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                  height: '22px',
                  whiteSpace: 'nowrap',
                  '& svg': {
                    fontSize: '13px',
                    marginRight: '4px',
                    color: '#1976d2'
                  }
                }}
              >
                <AccessTimeIcon fontSize="inherit" />
                {formattedDate}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default WhatsappContactBox;